import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import type { PaginatedResponse, Tool } from '@tools-beaver/shared';
import { apiClient } from '../api/client';
import { ToolList } from '../components/tools/ToolList';
import appConfig from '../../../../config.json';

export function Browse() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setQuery(q);
  }, [searchParams]);

  const { data, isLoading } = useQuery<PaginatedResponse<Tool>>({
    queryKey: ['tools', query, category, city, minPrice, maxPrice, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (category) params.set('category', category);
      if (city) params.set('city', city);
      if (minPrice) params.set('minPrice', minPrice);
      if (maxPrice) params.set('maxPrice', maxPrice);
      params.set('page', String(page));
      const { data } = await apiClient.get(`/tools/search?${params}`);
      return data.data;
    },
    staleTime: 30_000,
  });

  const clearFilters = () => {
    setQuery('');
    setCategory('');
    setCity('');
    setMinPrice('');
    setMaxPrice('');
    setPage(1);
  };

  const hasActiveFilters = query || category || city || minPrice || maxPrice;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Search bar */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-silver-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search for a tool…"
            className="input pl-10"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-3 rounded-2xl border text-sm font-medium
                      transition-all ${showFilters
                        ? 'bg-garden-500 text-white border-garden-500'
                        : 'bg-white text-silver-700 border-silver-200 hover:border-garden-400'}`}
        >
          <SlidersHorizontal size={16} />
          <span className="hidden sm:inline">Filters</span>
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-beaver-amber flex-shrink-0" />
          )}
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white rounded-3xl shadow-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-slide-up">
          <div>
            <label className="block text-xs font-medium text-silver-600 mb-1.5">Category</label>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
              className="input text-sm"
            >
              <option value="">All categories</option>
              {appConfig.toolCategories.map((cat) => (
                <option key={cat.slug} value={cat.slug}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-600 mb-1.5">City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => { setCity(e.target.value); setPage(1); }}
              placeholder="e.g. Austin"
              className="input text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-600 mb-1.5">Min price/day</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-silver-400 font-medium text-sm">$</span>
              <input
                type="number"
                min="0"
                value={minPrice}
                onChange={(e) => { setMinPrice(e.target.value); setPage(1); }}
                placeholder="0"
                className="input text-sm pl-7"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-600 mb-1.5">Max price/day</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-silver-400 font-medium text-sm">$</span>
              <input
                type="number"
                min="0"
                value={maxPrice}
                onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
                placeholder="Any"
                className="input text-sm pl-7"
              />
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mb-4">
          {query && (
            <span className="badge bg-silver-100 text-silver-700 gap-1">
              "{query}"
              <button onClick={() => setQuery('')} className="hover:text-red-500 ml-1">
                <X size={12} />
              </button>
            </span>
          )}
          {category && (
            <span className="badge bg-garden-100 text-garden-700 gap-1">
              {appConfig.toolCategories.find((c) => c.slug === category)?.name ?? category}
              <button onClick={() => setCategory('')} className="hover:text-red-500 ml-1">
                <X size={12} />
              </button>
            </span>
          )}
          {city && (
            <span className="badge bg-beaver-cream text-beaver-brown gap-1">
              📍 {city}
              <button onClick={() => setCity('')} className="hover:text-red-500 ml-1">
                <X size={12} />
              </button>
            </span>
          )}
          {(minPrice || maxPrice) && (
            <span className="badge bg-blue-50 text-blue-700 gap-1">
              💰 {minPrice ? `$${minPrice}` : '$0'} – {maxPrice ? `$${maxPrice}` : 'any'}
              <button onClick={() => { setMinPrice(''); setMaxPrice(''); }} className="hover:text-red-500 ml-1">
                <X size={12} />
              </button>
            </span>
          )}
          <button onClick={clearFilters} className="text-xs text-silver-400 hover:text-red-500 transition-colors">
            Clear all
          </button>
        </div>
      )}

      {/* Results */}
      <ToolList
        tools={data?.data ?? []}
        isLoading={isLoading}
        title={!isLoading && data ? `${data.total} tools available` : undefined}
        emptyMessage="No tools match your search. Try broadening your filters!"
      />

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: data.totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-9 h-9 rounded-xl text-sm font-medium transition-all ${
                p === page
                  ? 'bg-garden-500 text-white shadow-tim'
                  : 'bg-white text-silver-700 border border-silver-200 hover:border-garden-400'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
